/**
 * Macro arithmetic: scaling a reference portion, and summing a day.
 */
import { round } from './num';
import type { Macros } from '../data/schema';

export const ZERO: Macros = { kcal: 0, protein: 0, carbs: 0, fat: 0 };

/** Scale macros by a factor (e.g. 1.5 servings, or grams/100 for per-100 g data). */
export function scale(m: Macros, factor: number): Macros {
  if (!Number.isFinite(factor) || factor < 0) throw new RangeError('factor must be zero or greater');
  const out: Macros = {
    kcal: round(m.kcal * factor, 2),
    protein: round(m.protein * factor, 2),
    carbs: round(m.carbs * factor, 2),
    fat: round(m.fat * factor, 2),
  };
  if (m.fiber !== undefined) out.fiber = round(m.fiber * factor, 2);
  if (m.sugar !== undefined) out.sugar = round(m.sugar * factor, 2);
  if (m.sodium !== undefined) out.sodium = round(m.sodium * factor, 2);
  return out;
}

/** Macros for `grams` of a food whose macros are stated per 100 g. */
export function forGrams(per100g: Macros, grams: number): Macros {
  return scale(per100g, grams / 100);
}

export function add(a: Macros, b: Macros): Macros {
  const out: Macros = {
    kcal: round(a.kcal + b.kcal, 2),
    protein: round(a.protein + b.protein, 2),
    carbs: round(a.carbs + b.carbs, 2),
    fat: round(a.fat + b.fat, 2),
  };
  if (a.fiber !== undefined || b.fiber !== undefined) out.fiber = round((a.fiber ?? 0) + (b.fiber ?? 0), 2);
  if (a.sugar !== undefined || b.sugar !== undefined) out.sugar = round((a.sugar ?? 0) + (b.sugar ?? 0), 2);
  if (a.sodium !== undefined || b.sodium !== undefined) out.sodium = round((a.sodium ?? 0) + (b.sodium ?? 0), 2);
  return out;
}

export function total(items: { macros: Macros }[]): Macros {
  return items.reduce((acc, item) => add(acc, item.macros), { ...ZERO });
}

/**
 * Energy implied by the macros, using Atwater factors (4/4/9 kcal per gram).
 * Used to sanity-check a label whose stated calories disagree with its macros;
 * it is reported, never substituted.
 */
export function atwaterKcal(m: Macros): number {
  return round(m.protein * 4 + m.carbs * 4 + m.fat * 9, 1);
}

/** Share of energy from each macro, 0..1. Returns zeros when energy is zero. */
export function macroSplit(m: Macros): { protein: number; carbs: number; fat: number } {
  const energy = m.protein * 4 + m.carbs * 4 + m.fat * 9;
  if (energy <= 0) return { protein: 0, carbs: 0, fat: 0 };
  return {
    protein: round((m.protein * 4) / energy, 4),
    carbs: round((m.carbs * 4) / energy, 4),
    fat: round((m.fat * 9) / energy, 4),
  };
}

/** Remaining against a goal. Negative means over. */
export function remaining(goal: number | undefined, consumed: number): number | null {
  if (goal === undefined || !Number.isFinite(goal)) return null;
  return round(goal - consumed, 1);
}
